/**
 * Onyx welcome popup.
 *
 * Opens a native <dialog> once per browser after a delay set in theme settings.
 * Using showModal() gives the focus trap, the Escape handler and the inert
 * background for free, so there is no bespoke focus management to get wrong.
 *
 * The dialog is only in the DOM when the merchant has enabled it and the
 * template allows it; that decision is made in Liquid.
 */

const SELECTOR = '[data-onyx-welcome-popup]';

/**
 * @param {string} key - localStorage key.
 * @returns {boolean} True when the visitor has already seen the popup.
 */
function hasSeen(key) {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch (_) {
    // Private mode or blocked storage: treat as seen so we never nag.
    return true;
  }
}

/** @param {string} key - localStorage key. */
function markSeen(key) {
  try {
    window.localStorage.setItem(key, 'true');
  } catch (_) {
    // Nothing useful to do; the popup simply may reappear next visit.
  }
}

/** @param {HTMLDialogElement} dialog */
function setup(dialog) {
  const storageKey = dialog.dataset.storageKey ?? 'onyx-welcome-popup-seen';
  const delaySeconds = Number.parseInt(dialog.dataset.delay ?? '12', 10);
  const delay = Number.isFinite(delaySeconds) ? Math.max(delaySeconds, 0) * 1000 : 12000;
  const panel = dialog.querySelector('[data-onyx-welcome-popup-panel]');
  const closeButton = dialog.querySelector('[data-onyx-welcome-popup-close]');
  const message = dialog.querySelector('[data-onyx-welcome-popup-message]');

  const open = () => {
    if (dialog.open) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  };

  const close = () => {
    markSeen(storageKey);

    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  closeButton?.addEventListener('click', close);

  // Escape is handled natively by <dialog>; record the dismissal too.
  dialog.addEventListener('cancel', () => markSeen(storageKey));
  dialog.addEventListener('close', () => markSeen(storageKey));

  // Backdrop click: any pointer press that lands outside the panel.
  dialog.addEventListener('click', (event) => {
    if (!(panel instanceof HTMLElement)) return;
    if (event.target === dialog) close();
  });

  // Honeypot: a filled-in trap means a bot, so the submission never leaves.
  dialog.addEventListener('submit', (event) => {
    const form = event.target;

    if (form instanceof HTMLFormElement) {
      const trap = form.elements.namedItem('onyx_company');

      if (trap instanceof HTMLInputElement && trap.value.trim() !== '') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    markSeen(storageKey);
  });

  // A round trip from the form brings us back with #onyx-welcome-popup-form in
  // the URL: reopen so the visitor sees the result of what they just did.
  if (message instanceof HTMLElement && window.location.hash.startsWith(`#${dialog.id}`)) {
    open();
    message.focus({ preventScroll: true });
    return;
  }

  if (hasSeen(storageKey)) return;

  // Never stack on top of Shopify's cookie banner or an open drawer/modal: a
  // visitor who has not yet answered the consent banner, or who has the cart
  // open, must not get a second overlay. Wait until the way is clear.
  const consentVisible = () =>
    ['#shopify-pc__banner', '#shopify-pc__prefs'].some((selector) => {
      const element = document.querySelector(selector);
      if (!(element instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  const otherOverlayOpen = () =>
    Boolean(document.querySelector('dialog[open]:not([data-onyx-welcome-popup]), #cart-drawer[open]'));

  const openWhenClear = () => {
    if (hasSeen(storageKey)) return;
    if (consentVisible() || otherOverlayOpen()) {
      window.setTimeout(openWhenClear, 1000);
      return;
    }
    open();
  };

  window.setTimeout(openWhenClear, delay);
}

for (const dialog of document.querySelectorAll(SELECTOR)) {
  if (dialog instanceof HTMLDialogElement) setup(dialog);
}
